import * as React from "react";
import { useMemo, useState } from "react";
import {
  BrandFilterOption,
  FilterableCounts,
  FilterOptions,
  FilterQuery,
} from "../../../../../../microservices/functions/src/types/product_catalog/catalog_api_types";
import { ShowMoreButton } from "../show_more_button";

const maxShownBrands = 5;

const partitionBrandsByCount = (
  brands: BrandFilterOption[],
  filterableCounts: FilterableCounts["brandId"]
): [BrandFilterOption[], BrandFilterOption[]] => {
  const brandsSortedByCount = brands.sort(
    (b1, b2) => filterableCounts[b2.id] - filterableCounts[b1.id]
  );

  const topBrands = brandsSortedByCount.slice(0, maxShownBrands);
  const restBrands = brandsSortedByCount
    .slice(maxShownBrands)
    .sort((b1, b2) => {
      if (b1.name > b2.name) {
        return 1;
      } else if (b1.name < b2.name) {
        return -1;
      } else {
        return 0;
      }
    });

  return [topBrands, restBrands];
};

type BrandFilterControlsItemProps = {
  brand: BrandFilterOption;
  filterableCounts: FilterableCounts["brandId"];
  onChange: (newFilteredBrandIds: FilterQuery["brandId"]) => void;
  filteredBrands: FilterQuery["brandId"];
  landingPageFilterBrands: Array<number>;
};

const BrandFilterControlsItem = ({
  brand,
  filterableCounts,
  filteredBrands,
  landingPageFilterBrands,
  onChange,
}: BrandFilterControlsItemProps) => (
  <li key={brand.id}>
    <label className="false">
      <input
        type="checkbox"
        value={brand.id}
        checked={filteredBrands?.includes(brand.id) ?? false} // here the checked brand is controlled
        onChange={() => {
          const newBrandFilterControls = filteredBrands?.includes(brand.id)
            ? filteredBrands.filter((inFilter) => inFilter !== brand.id)
            : [...(filteredBrands ?? []), brand.id];
          onChange(newBrandFilterControls);
        }}
      />
      <span>
        {brand.name} <b>({filterableCounts[brand.id] ?? 0})</b>
      </span>
    </label>
  </li>
);

type FilterSidebarProps = {
  brands: FilterOptions["brands"];
  filterableCounts: FilterableCounts["brandId"];
  onChange: (newFilteredBrandIds: FilterQuery["brandId"]) => void;
  filteredBrands: FilterQuery["brandId"];
  landingPageFilterBrands: Array<number>;
};

export const BrandFilterControls = ({
  brands,
  filterableCounts,
  onChange,
  filteredBrands,
  landingPageFilterBrands,
}: FilterSidebarProps): JSX.Element => {
  const [allShown, setAllShown] = useState(false);

  const [topBrands, restBrands] = useMemo(
    () => partitionBrandsByCount(brands, filterableCounts),
    [brands, filterableCounts]
  );

  return (
    <fieldset>
      <strong className="title">Značka</strong>
      <ul>
        {topBrands.map((brand) => (
          <BrandFilterControlsItem
            key={brand.id}
            brand={brand}
            filterableCounts={filterableCounts}
            filteredBrands={filteredBrands}
            landingPageFilterBrands={landingPageFilterBrands}
            onChange={onChange}
          />
        ))}
        {allShown &&
          restBrands.map((brand) => (
            <BrandFilterControlsItem
              key={brand.id}
              brand={brand}
              filterableCounts={filterableCounts}
              filteredBrands={filteredBrands}
              landingPageFilterBrands={landingPageFilterBrands}
              onChange={onChange}
            />
          ))}
      </ul>
      {restBrands.length > 0 &&
        (allShown ? (
          <ShowMoreButton isExpanded onClick={() => setAllShown(false)}>
            Skrýt
          </ShowMoreButton>
        ) : (
          <ShowMoreButton onClick={() => setAllShown(true)}>
            Zobrazit další…
          </ShowMoreButton>
        ))}
    </fieldset>
  );
};
